import React, { memo, useState } from 'react';

import { AutoWizard as StepWizard, PageTitle } from '@dec/shared';
import { Button } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { Combobox, HookForm } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { NavStepWizard } from 'app/modules/Taxpayer/Components/nav-step-wizard';
import { NavWizard } from 'app/modules/Taxpayer/Components/nav-wizard';
import { ButtonGroup, Card, Col, Row } from 'react-bootstrap';
import { PersonPlus } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { useGetEnabledRegistrationFields } from '@/app/modules/Parameters/TaxpayerFieldRegistration/hooks';
import { type TaxpayerRegistrationFieldConfiguration } from '@/app/modules/Parameters/TaxpayerFieldRegistration/types';

import { useTranslateListAll } from '../../../../../components/translate/useTranslateList';
import { ObjectRequired } from '../../../../../core/validations/common';
import { useTaxpayerRequestStore } from '../../../stores/useTaxpayerRequestStore';
import { useTaxpayerRequestCapabilities } from '../../hooks';
import {
  Acceptance,
  Contact,
  Documents,
  GeneralData,
  Operations,
  ReportVector,
  Tax,
  TaxVector
} from '../../steps';
import { label } from '../../translation';

import '../../step.scss';

export const TaxpayerRegistrationContext =
  React.createContext<TaxpayerRegistrationFieldConfiguration>({ fields: [] });

interface WizardProps {
  conf: TaxpayerRegistrationFieldConfiguration;
}

const Wizard = ({ conf }: WizardProps) => {
  const { t } = useTranslation();
  const { loading, error, reportsEnabled, attachmentsEnabled } = useTaxpayerRequestCapabilities();

  if (loading || error) {
    return <></>;
  }

  const documentsStep = reportsEnabled ? 7 : 6;
  const acceptanceStep = documentsStep + (attachmentsEnabled ? 1 : 0);

  return (
    <TaxpayerRegistrationContext.Provider value={conf}>
      <StepWizard
        transitions={{}}
        isLazyMount={true}
        nav={
          <NavWizard>
            {(props: any) => {
              return (
                <>
                  <NavStepWizard {...props} stepKey={1} label={label(t, 'generalData')} />
                  <NavStepWizard {...props} stepKey={2} label={label(t, 'tax')} />
                  <NavStepWizard {...props} stepKey={3} label={label(t, 'contacts')} />
                  <NavStepWizard {...props} stepKey={4} label={label(t, 'operations')} />
                  <NavStepWizard {...props} stepKey={5} label={label(t, 'taxVector')} />
                  {reportsEnabled && (
                    <NavStepWizard {...props} stepKey={6} label={label(t, 'reportVector')} />
                  )}
                  {attachmentsEnabled && (
                    <NavStepWizard
                      {...props}
                      stepKey={documentsStep}
                      label={label(t, 'documents')}
                    />
                  )}
                  <NavStepWizard
                    {...props}
                    stepKey={acceptanceStep}
                    label={label(t, 'acceptance')}
                  />
                </>
              );
            }}
          </NavWizard>
        }
      >
        <GeneralData percentage={0} />
        <Tax percentage={25} />
        <Contact percentage={35} />
        <Operations percentage={45} />
        <TaxVector percentage={60} />
        {reportsEnabled && <ReportVector percentage={60} />}
        {attachmentsEnabled && <Documents percentage={85} />}
        <Acceptance percentage={95} />
      </StepWizard>
    </TaxpayerRegistrationContext.Provider>
  );
};

export const TaxpayerRequest = memo(function TaxpayerRequest() {
  // se procede a cargar la información de la configuración de campos
  const [{ data, loading, error }] = useGetEnabledRegistrationFields();

  const preferredLanguage = useTaxpayerRequestStore((s) => s.form.preferredLanguage);
  const langList = useTranslateListAll();
  const { t, i18n } = useTranslation();
  const [languageSelected, setLanguageSelected] = useState(false);
  const rules = yup.object().shape({
    locale: ObjectRequired
  });
  const navigate = useNavigate();

  if (!languageSelected) {
    return (
      <div className="col-12 col-md-6 col-lg-5 mx-auto">
        <Card className="p-2">
          <Card.Body>
            <HookForm
              nameForm={'formSelectLang'}
              validations={rules}
              onSubmit={() => {
                setLanguageSelected(true);
              }}
            >
              <Row>
                <Col>
                  <Combobox
                    code="id"
                    display="name"
                    data={langList}
                    label={label(t, 'locale')}
                    name="locale"
                    defaultValue={
                      preferredLanguage
                        ? langList.find((x) => x.id == preferredLanguage)
                        : undefined
                    }
                    whenChange={(e: any) => {
                      if (e?.id && i18n.language !== e.id) {
                        i18n.changeLanguage(e.id);
                      }
                    }}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <BtnToolbar>
                    <ButtonGroup>
                      <Button action={ButtonAction.cancel} onClick={() => navigate('/')} />
                    </ButtonGroup>
                    <ButtonGroup>
                      <Button
                        action={ButtonAction.next}
                        variant="primary"
                        form="formSelectLang"
                        type={'submit'}
                        text={t('common.button.continue')}
                      />
                    </ButtonGroup>
                  </BtnToolbar>
                </Col>
              </Row>
            </HookForm>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (loading || error) {
    return (
      <div className="container content-body">
        <Wizard conf={{ fields: [] }} />
      </div>
    );
  }

  // se invoca con los campos que se recien o con el conjunto vacio en caso de no tener resultados
  return (
    <div className="container content-body">
      <PageTitle title={t('taxpayer-request.label.taxpayerRequest')} icon={PersonPlus} />

      <Wizard conf={{ fields: data || [] }} />
    </div>
  );
});
